/**
 * Custom pet portrait builder — Step: design, preview & add to cart.
 *
 * One AI painting per pet photo, then an editable preview where each pet
 * and the name can be dragged, resized from a corner, rotated or nudged.
 * There's a background colour, four name fonts with above/below
 * placement, and a switch between the flat design and an "on the wall"
 * mockup of the chosen frame or canvas (real room photos from PSD mockups).
 *
 * The design is drawn by navabird-wall-art-renderer.js in design units
 * (always the same width, height from the print size's shape), so the
 * on-screen edit, the wall mockup and the full-size print file all come
 * from the same layout.
 *
 * Add to cart uploads the print file (sized for the chosen print size),
 * the wall mockup (cart image), a design preview and the original photos,
 * and adds the chosen Format/Size variant with their URLs as properties.
 *
 * The house portrait uses the same editor with options from its
 * [data-nb-wall-assets] JSON (all optional; without them it's the pet builder):
 *   painter: 'photopea'   paint with navabird-house-painter.js (no AI, no test mode)
 *   layout: 'house'       one wide painting, centred, text underneath
 *   maxPhotos, photoMaxDimension, enhance (null = no contrast lift),
 *   defaultBackground, copy: { photoLabel, nameProperty, loaderNote,
 *   loaderMessages, expectedSeconds }
 */
(function () {
  const MAX_PETS = 3;
  const PREVIEW_DESIGN_WIDTH = 900; // canvas px for the editable design view
  const PREVIEW_WALL_SIZE = 1000; // square wall mockup, canvas px
  // Print files follow Gelato's specs (support.gelato.com, Oct 2026):
  // metric product sizes, 150–300 DPI, 4 mm bleed on framed posters and a
  // 49 mm wrap per side on slim (2 cm) canvas. The extra area is filled by
  // mirroring the design's edges, so colour and fur continue seamlessly.
  const PRINT_MAX_LONG_SIDE = 3600; // px of the visible print; keeps uploads small
  const PRINT_MAX_DPI = 300;
  const PRINT_MIN_DPI = 150; // Gelato's minimum
  const GELATO_SIZES_MM = { '8x10': [200, 250], '12x16': [300, 400], '18x24': [450, 600], '24x32': [600, 800] };
  const FRAME_BLEED_MM = 4;
  const CANVAS_WRAP_MM = 49; // slim canvas: 34 mm side + 15 mm folded round the back
  // Every painting gets a light contrast lift (chosen by the store owner,
  // "B" in the 2026-10-15 comparison): the coat's shine and the eyes pop.
  const PET_ENHANCE = { contrast: 1.1 };
  const DEFAULT_BACKGROUND = '#ffffff';

  const SIZE_STEP = 0.05;
  const SIZE_MIN = 0.3;
  const SIZE_MAX = 2.5;
  const NUDGE_STEP = 20; // design units
  const UNDO_LIMIT = 30;

  const HANDLE_VISUAL_RADIUS = 6; // CSS px
  const HANDLE_HIT_RADIUS = 16; // CSS px
  const SELECTION_PAD = 6; // CSS px

  // TEST MODE — development/preview themes only. Instead of calling the
  // (paid) AI painter, every pet gets a finished sample painting after a
  // short pretend wait, so the builder can be clicked through for free.
  // Never on the published (live) theme: there Shopify.theme.role is
  // 'main'. Add ?real_paint=1 to the URL to use the real painter anyway.
  const TEST_MODE =
    !!(window.Shopify && window.Shopify.theme && window.Shopify.theme.role !== 'main') &&
    !/[?&]real_paint=1/.test(window.location.search);
  const TEST_FAKE_PAINT_MS = 4000;

  const GENERATION_FAILED_MESSAGE = "We couldn't create your design right now. Please try again in a few minutes.";
  const SAVE_FAILED_MESSAGE = "We couldn't save your design — please check your connection and try again.";
  const UPLOAD_MAX_BYTES = 2.8 * 1024 * 1024;
  const ORIGINAL_MAX_BYTES = 1.5 * 1024 * 1024;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Image failed to load: ${String(src).slice(0, 80)}`));
      img.src = src;
    });
  }

  // The AI painting's transparent canvas isn't reliably centred on the pet
  // (padding varies per generation), so the pet's actual opaque content is
  // what gets placed.
  const ALPHA_THRESHOLD = 10;
  function getOpaqueBounds(image) {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0);

    let data;
    try {
      data = ctx.getImageData(0, 0, image.width, image.height).data;
    } catch (err) {
      // Tainted canvas (cross-origin image without CORS) — use the full image.
      console.error('Could not read pixel data to trim pet art', err);
      return { x: 0, y: 0, w: image.width, h: image.height };
    }

    let minX = image.width;
    let minY = image.height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < image.height; y++) {
      for (let x = 0; x < image.width; x++) {
        if (data[(y * image.width + x) * 4 + 3] > ALPHA_THRESHOLD) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX || maxY < minY) return { x: 0, y: 0, w: image.width, h: image.height };
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  }

  function init() {
    const step = document.querySelector('.nb-wall-design[data-step="review"]');
    const assetsEl = document.querySelector('[data-nb-wall-assets]');
    const productEl = document.querySelector('[data-nb-wall-product]');
    if (!step || !assetsEl || !productEl || !window.NavaBirdWallArt) return;

    const Art = window.NavaBirdWallArt;
    const assets = JSON.parse(assetsEl.textContent);
    const productData = JSON.parse(productEl.textContent);
    const isHouse = assets.layout === 'house';
    if (Art.configure) Art.configure({ layout: assets.layout, subFont: assets.line2Font });
    const maxPets = assets.maxPhotos || MAX_PETS;
    const copy = assets.copy || {};
    const usePhotopea = assets.painter === 'photopea' && !!window.NavaBirdHousePainter;
    const enhanceOptions = assets.enhance === undefined ? PET_ENHANCE : assets.enhance;

    const $ = (sel) => step.querySelector(sel);
    const $$ = (sel) => Array.from(step.querySelectorAll(sel));

    const nameInput = $('[data-pet-name-input]');
    // Optional second, smaller text line (house portrait only).
    const line2Input = $('[data-line2-input]');
    // The text drawn on the design: line 1, plus "\n" + line 2 when there is one.
    function designText() {
      const second = line2Input ? line2Input.value.trim() : '';
      return second ? `${nameInput.value}\n${second}` : nameInput.value;
    }
    const addToCartBtn = $('[data-add-to-cart]');
    const errorEl = $('[data-cart-error]');
    const subheadingEl = $('[data-review-subheading]');
    const canvasEl = $('[data-review-canvas]');
    const overlayEl = $('[data-review-overlay-canvas]');
    const loadingEl = $('[data-review-loading]');
    const toolbar = $('[data-toolbar]');
    const viewSwitch = $('[data-tool-view]');
    const photoInput = $('[data-photo-input]');
    const petListEl = $('[data-pet-list]');
    const adjustTitleEl = $('[data-adjust-title]');
    const artSizeValueEl = $('[data-art-size-value]');
    const textSizeValueEl = $('[data-text-size-value]');
    const bgToolSwatch = $('[data-bg-tool-swatch]');

    // --- Design state ---------------------------------------------------
    let pets = []; // { image, bounds, adjust } in draw order
    const petAdjusts = Art.defaultPetAdjusts(1);
    const textAdjust = { ...Art.DEFAULT_TEXT_ADJUST, ...(assets.defaultFont ? { font: assets.defaultFont } : {}) };
    let background = assets.defaultBackground || DEFAULT_BACKGROUND;
    let paperImage = null;
    let selectedPet = 0;
    let viewMode = 'design'; // 'design' | 'wall'
    // In "On the wall": 0 = the editable wall mockup, 1.. = the room scenes
    // for the chosen frame (view only, swipe or arrows to flip through).
    let wallIndex = 0;
    let sceneView = false; // true while a room scene (not editable) is shown
    let activePanel = null;

    // Last render, for hit-testing and exports
    let lastDesign = null; // { canvas, petRects, textRect, height }
    let view = { scale: 1, ox: 0, oy: 0 }; // design units -> canvas px

    window.__nbWallDesign = { petAdjusts, textAdjust, get background() { return background; } };

    function wallState() {
      return window.__nbWallState || { ratio: 16 / 12, sizeInches: [12, 16], formatKey: 'wood' };
    }

    function currentPhotos() {
      const s = window.__instantArtworkState;
      return ((s && s.photos) || []).slice(0, maxPets);
    }

    function selectedAdjust() {
      return petAdjusts[selectedPet] || petAdjusts[0];
    }

    function ensurePetAdjusts(count) {
      if (petAdjusts.length === count) return;
      petAdjusts.splice(0, petAdjusts.length, ...Art.defaultPetAdjusts(count));
      if (selectedPet >= count) selectedPet = 0;
      undoStack.length = 0;
    }

    // --- Undo -----------------------------------------------------------
    const undoStack = [];
    function snapshot() {
      undoStack.push({ pets: petAdjusts.map((a) => ({ ...a })), text: { ...textAdjust }, background });
      if (undoStack.length > UNDO_LIMIT) undoStack.shift();
    }
    function undo() {
      const prev = undoStack.pop();
      if (!prev) return;
      if (prev.pets.length === petAdjusts.length) prev.pets.forEach((a, i) => Object.assign(petAdjusts[i], a));
      Object.assign(textAdjust, prev.text);
      background = prev.background;
      changed();
    }

    // --- AI generation -------------------------------------------------------
    const artCache = new Map(); // photo id -> Promise<Image>
    const loadedPhotoIds = new Set();
    let designTokenPromise = null;
    let generationStartedAt = 0;

    function blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read photo'));
        reader.readAsDataURL(blob);
      });
    }

    async function postGenerate(payload) {
      const res = await fetch(assets.generateApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      return { res, data };
    }

    function getDesignToken(petCount) {
      if (!designTokenPromise) {
        designTokenPromise = postGenerate({ start: true, pets: petCount }).then(({ res, data }) => {
          if (!res.ok || !data.designToken) throw new Error((res.status === 429 && data.error) || GENERATION_FAILED_MESSAGE);
          return data.designToken;
        });
        designTokenPromise.catch(() => {
          designTokenPromise = null;
        });
      }
      return designTokenPromise;
    }

    async function generatePetArt(photo, petCount) {
      const photoBase64 = await blobToBase64(photo.file);
      for (let attempt = 0; attempt < 2; attempt++) {
        const tokenPromise = getDesignToken(petCount);
        const designToken = await tokenPromise;
        const { res, data } = await postGenerate({ photoBase64, designToken });
        if (res.status === 409 && data.code === 'design_used_up') {
          if (designTokenPromise === tokenPromise) designTokenPromise = null;
          continue;
        }
        if (!res.ok || !data.image) throw new Error((res.status === 429 && data.error) || GENERATION_FAILED_MESSAGE);
        return loadImage(data.image);
      }
      throw new Error(GENERATION_FAILED_MESSAGE);
    }

    function getPetArtImage(photo, petCount) {
      if (artCache.has(photo.id)) return artCache.get(photo.id);
      if (usePhotopea) {
        // House portrait: painted in this browser from the store's PSD (free,
        // so no test mode). Resolves to a canvas with soft painted edges.
        const painting = window.NavaBirdHousePainter.paint(photo.file).catch((err) => {
          artCache.delete(photo.id);
          console.error('House painting failed', err);
          throw new Error(GENERATION_FAILED_MESSAGE);
        });
        artCache.set(photo.id, painting);
        return painting;
      }
      if (TEST_MODE && assets.testPaintingUrl) {
        const fake = new Promise((resolve) => setTimeout(resolve, TEST_FAKE_PAINT_MS)).then(() => loadImage(assets.testPaintingUrl));
        artCache.set(photo.id, fake);
        return fake;
      }
      const promise = generatePetArt(photo, petCount).catch((err) => {
        artCache.delete(photo.id);
        throw err;
      });
      artCache.set(photo.id, promise);
      return promise;
    }

    function getPetArtImages(photos) {
      const uncached = photos.filter((p) => !artCache.has(p.id)).length;
      if (uncached) generationStartedAt = Date.now();
      return Promise.all(photos.map((p) => getPetArtImage(p, uncached))).then((images) => {
        photos.forEach((p) => loadedPhotoIds.add(p.id));
        return images;
      });
    }

    // Photopea takes a few seconds to start: begin loading it (and the
    // template) as soon as the customer reaches the photo step.
    const uploadStepEl = document.querySelector('[data-step="upload"]');
    if (usePhotopea && uploadStepEl) {
      uploadStepEl.addEventListener('instant-artwork:step-shown', () => window.NavaBirdHousePainter.warmUp());
    }

    // Start painting as soon as the photo step is done (overlaps with the name step).
    const nameStepEl = document.querySelector('[data-step="name"]');
    if (nameStepEl) {
      nameStepEl.addEventListener('instant-artwork:step-shown', () => {
        const photos = currentPhotos();
        if (photos.length) getPetArtImages(photos).catch(() => {});
      });
    }

    // --- Static assets (fonts + paper) ----------------------------------
    let assetsPromise = null;
    function prepareAssets() {
      if (!assetsPromise) {
        assetsPromise = Promise.all([
          Art.loadFonts(assets),
          // No paper grain when the page passes none (house: plain white)
          assets.paperUrl
            ? loadImage(assets.paperUrl).then(
                (img) => (paperImage = img),
                () => null
              )
            : null,
          ...Object.entries(assets.mockups || {}).map(([key, m]) =>
            Promise.all([loadImage(m.url), m.overlay ? loadImage(m.overlay.url).catch(() => null) : null]).then(
              ([room, overlay]) => (mockupImages[key] = { room, overlay }),
              () => null // falls back to the drawn mockup
            )
          ),
        ]);
      }
      return assetsPromise;
    }

    // --- Rendering --------------------------------------------------------
    function renderDesignAt(outputWidth, ratio = wallState().ratio) {
      return Art.renderDesign({
        ratio,
        background,
        paperImage,
        pets,
        name: designText(),
        text: textAdjust,
        outputWidth,
      });
    }

    // Real room-photo mockups (from PSDs), per format, when available;
    // other formats use the drawn wall mockup.
    const mockupImages = {}; // format key -> { room, overlay }
    function photoMockup() {
      const key = wallState().formatKey;
      const m = (assets.mockups || {})[key];
      const imgs = mockupImages[key];
      if (!m || !imgs || !imgs.room) return null;
      return {
        ...m,
        room: imgs.room,
        overlay: m.overlay && imgs.overlay ? { ...m.overlay, image: imgs.overlay } : null,
      };
    }

    function renderMockup(size, designWidth) {
      const photo = photoMockup();
      if (photo) {
        // Laid out at the frame window's own shape, so the print fills it.
        const [, , w, h] = photo.window;
        const design = renderDesignAt(Math.round(w * 2), h / w);
        return {
          design,
          ...Art.renderPhotoMockup({
            design: design.canvas,
            room: photo.room,
            window: photo.window,
            blend: photo.blend,
            brighten: photo.brighten,
            overlay: photo.overlay,
          }),
        };
      }
      const design = renderDesignAt(designWidth);
      return { design, ...Art.renderWallMockup({ design: design.canvas, format: wallState().formatKey, size }) };
    }

    // Fast synchronous redraw from what's loaded (drag frames, button taps).
    function redraw() {
      if (!pets.length) return;
      const scenes = scenesForFormat();
      if (wallIndex > scenes.length) wallIndex = 0;
      const scene = viewMode === 'wall' && wallIndex > 0 ? scenes[wallIndex - 1] : null;
      sceneView = !!(scene && sceneLoaded[scene.id]);
      if (sceneView) {
        lastDesign = renderDesignAt(700, scene.ratio);
        copyToCanvas(renderSceneCanvas(scene, sceneLoaded[scene.id]));
      } else if (viewMode === 'wall') {
        const { design, canvas, designRect } = renderMockup(PREVIEW_WALL_SIZE, 700);
        lastDesign = design;
        copyToCanvas(canvas);
        view = { scale: designRect.w / Art.DESIGN_WIDTH, ox: designRect.x, oy: designRect.y };
      } else {
        lastDesign = renderDesignAt(PREVIEW_DESIGN_WIDTH);
        copyToCanvas(lastDesign.canvas);
        view = { scale: PREVIEW_DESIGN_WIDTH / Art.DESIGN_WIDTH, ox: 0, oy: 0 };
      }
      drawOverlay();
      syncSceneNav();
    }


    // --- Room scenes inside the preview ("On the wall") -------------------
    const sceneNav = $('[data-scene-nav]');
    const sceneDots = $('[data-scene-dots]');
    const designStep = step;
    function syncSceneNav() {
      const total = 1 + scenesForFormat().length;
      const show = viewMode === 'wall' && total > 1 && !canvasEl.hidden;
      sceneNav.hidden = !show;
      designStep.classList.toggle('is-wall-view', viewMode === 'wall');
      designStep.classList.toggle('is-scene-view', sceneView);
      if (!show) return;
      if (sceneDots.childElementCount !== total) {
        sceneDots.innerHTML = '';
        for (let i = 0; i < total; i++) sceneDots.appendChild(document.createElement('span'));
      }
      Array.from(sceneDots.children).forEach((d, i) => d.classList.toggle('is-active', i === wallIndex));
    }
    function goToScene(next) {
      const scenes = scenesForFormat();
      const total = 1 + scenes.length;
      wallIndex = (next + total) % total;
      const scene = wallIndex > 0 ? scenes[wallIndex - 1] : null;
      if (scene && !sceneLoaded[scene.id]) {
        loadScene(scene).then(() => redraw(), () => {});
      }
      if (activePanel === 'text' || activePanel === 'adjust') setActivePanel(null, { scroll: false });
      redraw();
    }
    $('[data-scene-prev]').addEventListener('click', () => goToScene(wallIndex - 1));
    $('[data-scene-next]').addEventListener('click', () => goToScene(wallIndex + 1));
    // Swipe on a room scene to flip (the editable mockup keeps drag-to-move).
    let swipeStart = null;

    function copyToCanvas(source) {
      canvasEl.width = source.width;
      canvasEl.height = source.height;
      canvasEl.getContext('2d').drawImage(source, 0, 0);
    }

    function designRectToCanvas(r) {
      if (!r) return null;
      return { x: view.ox + r.x * view.scale, y: view.oy + r.y * view.scale, w: r.w * view.scale, h: r.h * view.scale };
    }

    function canvasToScreenRatio() {
      const rect = canvasEl.getBoundingClientRect();
      return rect.width ? canvasEl.width / rect.width : 1;
    }

    function paddedRect(rect) {
      if (!rect) return null;
      const pad = SELECTION_PAD * canvasToScreenRatio();
      const x = Math.max(0, rect.x - pad);
      const y = Math.max(0, rect.y - pad);
      const right = Math.min(canvasEl.width, rect.x + rect.w + pad);
      const bottom = Math.min(canvasEl.height, rect.y + rect.h + pad);
      return { x, y, w: right - x, h: bottom - y };
    }

    function activeRectCanvas() {
      if (!lastDesign) return null;
      if (activePanel === 'text') return designRectToCanvas(lastDesign.textRect);
      if (activePanel === 'adjust') return designRectToCanvas(lastDesign.petRects[selectedPet]);
      return null;
    }

    // Selection box and snap guide colour: the theme's accent (NavaBird blue).
    const ACCENT = (() => {
      const rgb = getComputedStyle(document.documentElement).getPropertyValue('--color-base-accent-1').trim();
      return rgb ? `rgb(${rgb})` : '#89c9f4';
    })();

    function drawOverlay() {
      overlayEl.width = canvasEl.width;
      overlayEl.height = canvasEl.height;
      const ctx = overlayEl.getContext('2d');
      ctx.clearRect(0, 0, overlayEl.width, overlayEl.height);
      if (sceneView) return; // room scenes are view only
      if (viewMode === 'design') drawWatermark(ctx);
      drawSnapGuide(ctx);
      const rect = paddedRect(activeRectCanvas());
      if (!rect) return;
      const ratio = canvasToScreenRatio();
      ctx.save();
      ctx.lineWidth = 2 * ratio;
      ctx.strokeStyle = ACCENT;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(rect.x, rect.y, rect.w, rect.h, 8 * ratio);
      else ctx.rect(rect.x, rect.y, rect.w, rect.h);
      ctx.stroke();
      ctx.restore();
      corners(rect).forEach(([cx, cy]) => {
        ctx.beginPath();
        ctx.arc(cx, cy, HANDLE_VISUAL_RADIUS * ratio, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.lineWidth = 2.5 * ratio;
        ctx.strokeStyle = ACCENT;
        ctx.stroke();
      });
    }

    function corners(r) {
      return [
        [r.x, r.y],
        [r.x + r.w, r.y],
        [r.x, r.y + r.h],
        [r.x + r.w, r.y + r.h],
      ];
    }

    // Room scenes for the big gallery: the customer's portrait in each scene
    // whose frame matches theirs. Scene photos load only once a painting
    // exists (they aren't needed before), then stay cached.
    const sceneImages = {}; // scene id -> Promise<{ bg, mask, overlays }>
    const sceneLoaded = {}; // scene id -> { bg, mask, overlays } once ready
    function loadScene(scene) {
      if (!sceneImages[scene.id]) {
        sceneImages[scene.id] = Promise.all([
          loadImage(scene.bg),
          loadImage(scene.mask),
          ...(scene.overlays || []).map((o) => loadImage(o.url)),
        ]).then(([bg, mask, ...overlays]) => (sceneLoaded[scene.id] = { bg, mask, overlays }));
        sceneImages[scene.id].catch(() => delete sceneImages[scene.id]);
      }
      return sceneImages[scene.id];
    }

    // A scene's picture of the current design. Wrapped-canvas scenes get the
    // design with extra background around it for the canvas's sides.
    function renderSceneCanvas(scene, images) {
      const W = 700;
      if (!scene.wrapBox) {
        return Art.renderScene({ design: renderDesignAt(W, scene.ratio).canvas, scene, images });
      }
      const padUnits = 0.18 * Art.DESIGN_WIDTH;
      const design = Art.renderDesign({
        ratio: scene.ratio,
        background,
        paperImage,
        pets,
        name: designText(),
        text: textAdjust,
        outputWidth: W,
        pad: padUnits,
      }).canvas;
      return Art.renderScene({ design, scene, images, pad: Math.round((padUnits / Art.DESIGN_WIDTH) * W) });
    }

    function scenesForFormat() {
      const key = wallState().formatKey;
      return (assets.scenes || []).filter((sc) => sc.frame === key);
    }

    // Main mockup first, then the matching scenes. Debounced: runs once an
    // edit settles, not on every drag frame.
    let galleryTimer = null;
    let galleryRun = 0;
    function syncGallery() {
      clearTimeout(galleryTimer);
      galleryTimer = setTimeout(async () => {
        if (!window.NbWallGallery || !pets.length) return;
        const run = ++galleryRun;
        const urls = [renderMockup(1000, 700).canvas.toDataURL('image/jpeg', 0.85)];
        // Everything is rendered first and the gallery changes once, so
        // switching frames doesn't drop and re-add slides (a visible flash).
        for (const scene of scenesForFormat()) {
          let images;
          try {
            images = await loadScene(scene);
          } catch (err) {
            continue; // a scene that can't load is just skipped
          }
          if (run !== galleryRun) return; // a newer edit took over
          urls.push(renderSceneCanvas(scene, images).toDataURL('image/jpeg', 0.85));
          await new Promise((r) => setTimeout(r, 0)); // keep the page responsive
        }
        if (run === galleryRun) window.NbWallGallery.sync(urls);
      }, 350);
    }

    // After any edit: redraw, refresh the panels and the gallery.
    function changed() {
      syncControls();
      redraw();
      syncGallery();
    }

    // --- Loader -----------------------------------------------------------
    let loaderTimers = [];
    function stopLoader() {
      loaderTimers.forEach((t) => clearInterval(t));
      loaderTimers = [];
      loadingEl.classList.remove('is-generating');
    }

    function showLoader(photos) {
      const pending = photos.filter((p) => !loadedPhotoIds.has(p.id));
      stopLoader();
      loadingEl.classList.remove('is-error');
      loadingEl.classList.add('is-generating');
      loadingEl.innerHTML = `
        <div class="ia-gen">
          <div class="ia-gen__photo">
            <img class="ia-gen__img" alt="">
            <span class="ia-gen__streak" aria-hidden="true"></span>
            <svg class="ia-gen__brush" viewBox="0 0 48 48" aria-hidden="true">
              <path d="M30 6l12 12-16 16-12-12z" fill="#ffffff" stroke="#1a1a1a" stroke-width="2.5" stroke-linejoin="round"/>
              <path d="M14 22l12 12-4 4c-3 3-8 3-11 0s-3-8 0-11z" fill="#89c9f4" stroke="#1a1a1a" stroke-width="2.5" stroke-linejoin="round"/>
              <path d="M11 37c-3 1-5 3-6 6 3-1 5-3 6-6z" fill="#89c9f4" stroke="#1a1a1a" stroke-width="2" stroke-linejoin="round"/>
            </svg>
          </div>
          <p class="ia-gen__msg" aria-live="polite"></p>
          <div class="ia-gen__bar" aria-hidden="true"><span class="ia-gen__fill"></span></div>
          <p class="ia-gen__note"></p>
        </div>`;
      loadingEl.querySelector('.ia-gen__note').textContent =
        copy.loaderNote ||
        `${pending.length > 1 ? `Painting your ${pending.length} pets` : "Painting your pet's portrait"} — this usually takes about 30 seconds`;
      loadingEl.querySelector('.ia-gen__img').src = (pending[0] || photos[0]).previewUrl;
      loadingEl.hidden = false;

      const msgEl = loadingEl.querySelector('.ia-gen__msg');
      const fillEl = loadingEl.querySelector('.ia-gen__fill');
      const pet = nameInput.value.trim() || 'your pet';
      const messages = copy.loaderMessages || [
        `Setting up the easel for ${pet}…`,
        'Mixing the perfect colors…',
        'Painting every little hair…',
        'Adding sparkle to those eyes…',
        `Asking ${pet} to hold that pose…`,
      ];
      let i = 0;
      const showMessage = () => {
        msgEl.classList.remove('is-in');
        void msgEl.offsetWidth;
        msgEl.textContent = messages[i % messages.length];
        msgEl.classList.add('is-in');
        i += 1;
      };
      showMessage();
      loaderTimers.push(setInterval(showMessage, 2800));
      const tick = () => {
        const elapsed = Date.now() - (generationStartedAt || Date.now());
        const tau = (copy.expectedSeconds || 30) * 400; // ms; 30 s -> 12000
        fillEl.style.width = `${Math.max(4, 95 * (1 - Math.exp(-elapsed / tau))).toFixed(1)}%`;
      };
      tick();
      loaderTimers.push(setInterval(tick, 200));
    }

    // Small paw-print burst the first time the painting appears.
    function celebrate() {
      if (isHouse) return; // paw prints are for pets
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const stack = canvasEl.parentElement;
      const PAW_COUNT = 10;
      for (let i = 0; i < PAW_COUNT; i++) {
        const paw = document.createElement('span');
        paw.className = 'instant-artwork__paw-burst';
        const angle = (Math.PI * 2 * i) / PAW_COUNT + (Math.random() - 0.5) * 0.5;
        const distance = 90 + Math.random() * 60;
        paw.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
        paw.style.setProperty('--dy', `${Math.sin(angle) * distance}px`);
        paw.style.setProperty('--rot', `${Math.round((Math.random() - 0.5) * 90)}deg`);
        paw.style.animationDelay = `${Math.round(Math.random() * 120)}ms`;
        stack.appendChild(paw);
        paw.addEventListener('animationend', () => paw.remove());
      }
    }

    function showError(message) {
      stopLoader();
      canvasEl.hidden = true;
      overlayEl.hidden = true;
      toolbar.hidden = true;
      viewSwitch.hidden = true;
      setActivePanel(null);
      loadingEl.classList.add('is-error');
      loadingEl.innerHTML = '';
      const text = document.createElement('p');
      text.textContent = message;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'instant-artwork__retry';
      retry.textContent = 'Try again';
      retry.addEventListener('click', renderPreview);
      loadingEl.append(text, retry);
      loadingEl.hidden = false;
    }

    // Adjusted copy of each painting, made once (not per redraw).
    const enhancedCache = new WeakMap();
    function enhanced(image) {
      if (!enhanceOptions) return image;
      if (!enhancedCache.has(image)) enhancedCache.set(image, Art.enhancePet(image, enhanceOptions));
      return enhancedCache.get(image);
    }

    // --- Full render (waits for the AI art) -------------------------------
    let renderToken = 0;
    let artReady = false;
    let shownKey = null;

    async function renderPreview() {
      const photos = currentPhotos();
      if (!photos.length) return;
      const key = photos.map((p) => p.id).join('|');
      ensurePetAdjusts(photos.length);
      const myToken = ++renderToken;
      const isNewSet = shownKey !== key;
      const needsGeneration = photos.some((p) => !loadedPhotoIds.has(p.id));

      if (isNewSet) {
        artReady = false;
        updateButton();
      }
      if (isNewSet && needsGeneration) {
        getPetArtImages(photos).catch(() => {});
        showLoader(photos);
        canvasEl.hidden = true;
        overlayEl.hidden = true;
      }

      try {
        const [images] = await Promise.all([getPetArtImages(photos), prepareAssets()]);
        if (myToken !== renderToken) return;
        pets = images.map((image, i) => ({
          image: enhanced(image),
          bounds: getOpaqueBounds(image),
          adjust: petAdjusts[i],
        }));
        stopLoader();
        loadingEl.hidden = true;
        canvasEl.hidden = false;
        overlayEl.hidden = false;
        toolbar.hidden = false;
        viewSwitch.hidden = false;
        syncArButton();
        shownKey = key;
        artReady = true;
        updateButton();
        changed();
        if (isNewSet && needsGeneration) celebrate();
        photos.forEach((p) => uploadOriginal(p).catch(() => {}));
      } catch (err) {
        if (myToken !== renderToken) return;
        console.error('Portrait preview failed', err);
        showError(err.message || GENERATION_FAILED_MESSAGE);
      }
    }

    step.addEventListener('instant-artwork:step-shown', () => {
      renderPreview();
      buildSizePanel();
    });

    let nameTimer = null;
    [nameInput, line2Input].filter(Boolean).forEach((input) =>
      input.addEventListener('input', () => {
        syncFontSamples();
        clearTimeout(nameTimer);
        nameTimer = setTimeout(() => {
          redraw();
          syncGallery();
        }, 120);
      })
    );

    // Format/size changed (step 1 or the Style & size panel): new shape.
    document.addEventListener('nb-wall:selection-changed', () => {
      wallIndex = 0;
      if (viewMode === 'wall') scenesForFormat().forEach((sc) => loadScene(sc).catch(() => {}));
      updateButton();
      if (pets.length) changed();
      buildSizePanel();
    });

    // --- Pointer: tap to select, drag to move, corner handle to resize ----
    function screenToCanvas(clientX, clientY) {
      const rect = canvasEl.getBoundingClientRect();
      return {
        x: (clientX - rect.left) * (canvasEl.width / rect.width),
        y: (clientY - rect.top) * (canvasEl.height / rect.height),
      };
    }

    function screenToDesign(clientX, clientY) {
      const p = screenToCanvas(clientX, clientY);
      return { x: (p.x - view.ox) / view.scale, y: (p.y - view.oy) / view.scale };
    }

    function inRect(p, r) {
      return !!r && p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
    }

    function petAt(p) {
      if (!lastDesign) return -1;
      for (let i = lastDesign.petRects.length - 1; i >= 0; i--) {
        if (inRect(p, lastDesign.petRects[i])) return i;
      }
      return -1;
    }

    function hitHandle(clientX, clientY) {
      const rect = paddedRect(activeRectCanvas());
      if (!rect) return null;
      const pt = screenToCanvas(clientX, clientY);
      const r = HANDLE_HIT_RADIUS * canvasToScreenRatio();
      return corners(rect).some(([cx, cy]) => Math.hypot(pt.x - cx, pt.y - cy) <= r) ? rect : null;
    }

    // Pets may hang off the edges (a chest running off the bottom is the
    // look) but at least a quarter stays on the print; the name stays fully on.
    function moveWithin(adjust, rect, dx, dy, isText) {
      if (!rect || !lastDesign) {
        adjust.offsetX += dx;
        adjust.offsetY += dy;
        return;
      }
      const W = Art.DESIGN_WIDTH;
      const H = lastDesign.height;
      const keepX = isText ? rect.w : rect.w * 0.25;
      const keepY = isText ? rect.h : rect.h * 0.25;
      const minDx = keepX - rect.w - rect.x;
      const maxDx = W - keepX - rect.x;
      const minDy = keepY - rect.h - rect.y;
      const maxDy = H - keepY - rect.y;
      adjust.offsetX += clamp(dx, Math.min(0, minDx), Math.max(0, maxDx));
      adjust.offsetY += clamp(dy, Math.min(0, minDy), Math.max(0, maxDy));
    }

    let drag = null; // { target: 'text' | index, last: {x,y} }
    let resize = null; // { adjust, startScale, startDist, center }
    let raf = null;
    function scheduleRedraw() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        syncControls();
        redraw();
      });
    }

    canvasEl.addEventListener('pointerdown', (event) => {
      if (canvasEl.hidden || !lastDesign) return;
      if (sceneView) {
        swipeStart = { x: event.clientX, y: event.clientY };
        canvasEl.setPointerCapture(event.pointerId);
        return;
      }
      const handle = hitHandle(event.clientX, event.clientY);
      if (handle) {
        snapshot();
        const adjust = activePanel === 'text' ? textAdjust : selectedAdjust();
        const center = { x: handle.x + handle.w / 2, y: handle.y + handle.h / 2 };
        const pt = screenToCanvas(event.clientX, event.clientY);
        resize = { adjust, startScale: adjust.scale, startDist: Math.max(1, Math.hypot(pt.x - center.x, pt.y - center.y)), center };
        canvasEl.classList.add('is-dragging');
        canvasEl.setPointerCapture(event.pointerId);
        return;
      }

      const p = screenToDesign(event.clientX, event.clientY);
      const onText = inRect(p, lastDesign.textRect);
      const petIndex = onText ? -1 : petAt(p);
      if (!onText && petIndex === -1) {
        if (activePanel === 'text' || activePanel === 'adjust') setActivePanel(null);
        return;
      }
      if (!onText) selectedPet = petIndex;
      snapshot();
      const panel = onText ? 'text' : 'adjust';
      if (activePanel !== panel) setActivePanel(panel, { scroll: false });
      else {
        syncControls();
        drawOverlay();
      }
      drag = startDrag(onText ? 'text' : petIndex, p, event.pointerType === 'touch');
      canvasEl.style.cursor = '';
      canvasEl.classList.add('is-dragging');
      canvasEl.setPointerCapture(event.pointerId);
    });

    canvasEl.addEventListener('pointermove', (event) => {
      if (sceneView) {
        canvasEl.style.cursor = 'grab';
        return;
      }
      if (resize) {
        const pt = screenToCanvas(event.clientX, event.clientY);
        const dist = Math.max(1, Math.hypot(pt.x - resize.center.x, pt.y - resize.center.y));
        resize.adjust.scale = clamp(resize.startScale * (dist / resize.startDist), SIZE_MIN, SIZE_MAX);
        scheduleRedraw();
        return;
      }
      if (drag) {
        const p = screenToDesign(event.clientX, event.clientY);
        const dx = p.x - drag.last.x;
        const dy = p.y - drag.last.y;
        drag.last = p;
        const isText = drag.target === 'text';
        const adjust = isText ? textAdjust : petAdjusts[drag.target];
        const rect = isText ? lastDesign.textRect : lastDesign.petRects[drag.target];
        // Move from the unsnapped position, then snap (so leaving a snap
        // feels natural and nothing drifts).
        adjust.offsetX = drag.rawX;
        const rawRect = rect && { ...rect, x: rect.x + (drag.rawX - drag.appliedX) };
        moveWithin(adjust, rawRect, dx, dy, isText);
        drag.rawX = adjust.offsetX;
        applySnap(drag, adjust);
        scheduleRedraw();
        return;
      }
      if (!lastDesign) return;
      if (hitHandle(event.clientX, event.clientY)) {
        canvasEl.style.cursor = 'nwse-resize';
      } else {
        const p = screenToDesign(event.clientX, event.clientY);
        canvasEl.style.cursor = inRect(p, lastDesign.textRect) || petAt(p) !== -1 ? 'grab' : 'default';
      }
    });

    // --- Snap to centre (portrait only) -----------------------------------
    // While dragging, the pet or the name snaps to the portrait's centre line
    // (and the name to a pet's centre, a pet to the name's), with a blue
    // guide line while it's snapped. A slightly bigger snap zone for fingers.
    const SNAP_PX = { mouse: 8, touch: 14 };
    function startDrag(target, point, touch) {
      const isText = target === 'text';
      const adjust = isText ? textAdjust : petAdjusts[target];
      const rect = isText ? lastDesign.textRect : lastDesign.petRects[target];
      const targets = [Art.DESIGN_WIDTH / 2];
      if (isText) lastDesign.petRects.forEach((r) => targets.push(r.x + r.w / 2));
      else if (lastDesign.textRect) targets.push(lastDesign.textRect.x + lastDesign.textRect.w / 2);
      return {
        target,
        last: point,
        touch,
        rawX: adjust.offsetX,
        appliedX: adjust.offsetX,
        startOffset: adjust.offsetX,
        startCenter: rect ? rect.x + rect.w / 2 : Art.DESIGN_WIDTH / 2,
        targets,
        snapX: null,
      };
    }

    function applySnap(d, adjust) {
      const designPerScreenPx = canvasToScreenRatio() / view.scale;
      const zone = (d.touch ? SNAP_PX.touch : SNAP_PX.mouse) * designPerScreenPx;
      const center = d.startCenter + (d.rawX - d.startOffset);
      let best = null;
      d.targets.forEach((t) => {
        const dist = Math.abs(t - center);
        if (dist <= zone && (!best || dist < best.dist)) best = { t, dist };
      });
      const wasSnapped = d.snapX !== null;
      if (best) {
        adjust.offsetX = d.rawX + (best.t - center);
        d.snapX = best.t;
        if (!wasSnapped && navigator.vibrate) navigator.vibrate(8); // Android only
      } else {
        adjust.offsetX = d.rawX;
        d.snapX = null;
      }
      d.appliedX = adjust.offsetX;
    }

    // Subtle repeated "NavaBird · preview" over the editable design, so a
    // screenshot of it isn't a clean copy. Drawn on the overlay layer only:
    // the print file, cart picture and room scenes stay clean.
    const HEADING_FONT =
      getComputedStyle(document.documentElement).getPropertyValue('--font-heading-family').trim() || "'NBW Modern', sans-serif";
    function drawWatermark(ctx) {
      const W = overlayEl.width;
      const H = overlayEl.height;
      const ratio = canvasToScreenRatio();
      const fontPx = 13 * ratio;
      const text = 'NavaBird · preview';
      ctx.save();
      ctx.font = `700 ${fontPx}px ${HEADING_FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const stepX = ctx.measureText(text).width + 46 * ratio;
      const stepY = 64 * ratio;
      ctx.translate(W / 2, H / 2);
      ctx.rotate((-24 * Math.PI) / 180);
      const span = Math.hypot(W, H);
      let row = 0;
      for (let y = -span / 2; y <= span / 2; y += stepY, row++) {
        const shift = (row % 2) * (stepX / 2);
        for (let x = -span / 2 - stepX; x <= span / 2 + stepX; x += stepX) {
          // light and dark copies, so it reads on white backgrounds and dark fur
          ctx.fillStyle = 'rgba(255, 255, 255, 0.16)';
          ctx.fillText(text, x + shift, y);
          ctx.fillStyle = 'rgba(40, 30, 25, 0.07)';
          ctx.fillText(text, x + shift + ratio, y + ratio);
        }
      }
      ctx.restore();
    }

    function drawSnapGuide(ctx) {
      if (!drag || drag.snapX === null || !lastDesign) return;
      const ratio = canvasToScreenRatio();
      const x = view.ox + drag.snapX * view.scale;
      ctx.save();
      ctx.strokeStyle = ACCENT;
      ctx.lineWidth = 1.5 * ratio;
      ctx.setLineDash([6 * ratio, 4 * ratio]);
      ctx.beginPath();
      ctx.moveTo(x, view.oy);
      ctx.lineTo(x, view.oy + lastDesign.height * view.scale);
      ctx.stroke();
      ctx.restore();
    }

    function endDrag(event) {
      if (swipeStart && event) {
        const dx = event.clientX - swipeStart.x;
        const dy = event.clientY - swipeStart.y;
        swipeStart = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) goToScene(wallIndex + (dx < 0 ? 1 : -1));
        return;
      }
      swipeStart = null;
      if (!drag && !resize) return;
      drag = null;
      resize = null;
      canvasEl.classList.remove('is-dragging');
      drawOverlay(); // clears the snap guide
      syncGallery();
    }
    canvasEl.addEventListener('pointerup', endDrag);
    canvasEl.addEventListener('pointercancel', endDrag);

    // No right-click "Save image" / long-press save on the preview and the
    // product photos (screenshots can't be blocked, but this removes the
    // one-tap way to save).
    const noSaveRoots = [step.querySelector('.instant-artwork__canvas-stack'), document.querySelector('.product__media-wrapper')].filter(Boolean);
    noSaveRoots.forEach((root) => {
      root.classList.add('nb-no-save');
      root.addEventListener('contextmenu', (e) => e.preventDefault());
      root.addEventListener('dragstart', (e) => e.preventDefault());
    });

    // --- "See it on your wall" (iPhone AR Quick Look; TEST) ----------------
    // Shown only where Safari can open AR (iPhone/iPad), or with ?ar_debug
    // (downloads the 3D file instead, for testing on a computer).
    const arBtn = $('[data-ar-btn]');
    const arLabel = $('[data-ar-label]');
    const arDebug = /[?&]ar_debug=1/.test(window.location.search);
    const arAnchor = document.createElement('a');
    const arSupported = arDebug || !!(arAnchor.relList && arAnchor.relList.supports && arAnchor.relList.supports('ar'));
    const arTip = $('[data-ar-tip]');
    function syncArButton() {
      arBtn.hidden = !arSupported || canvasEl.hidden || wallState().formatKey === 'digital';
      if (arTip) arTip.hidden = arBtn.hidden;
    }
    arBtn.addEventListener('click', async () => {
      if (arBtn.disabled) return;
      arBtn.disabled = true;
      arLabel.textContent = 'Opening…';
      try {
        if (!window.NavaBirdAR) await import(assets.arModuleUrl);
        const st = wallState();
        const design = renderDesignAt(1400, st.sizeInches[1] / st.sizeInches[0]).canvas;
        await window.NavaBirdAR.open({ designCanvas: design, sizeInches: st.sizeInches, formatKey: st.formatKey, background, download: arDebug });
      } catch (err) {
        console.error('AR preview failed', err);
        setError("Couldn't open the wall preview on this device.");
      } finally {
        arBtn.disabled = false;
        arLabel.textContent = 'See it on your wall';
      }
    });
    document.addEventListener('nb-wall:selection-changed', syncArButton);

    // --- Panels -------------------------------------------------------------
    const panels = Object.fromEntries($$('[data-panel]').map((el) => [el.dataset.panel, el]));
    const toolBtns = Object.fromEntries($$('[data-tool]').map((el) => [el.dataset.tool, el]));

    function setActivePanel(name, { scroll = true } = {}) {
      const next = name && activePanel === name ? null : name;
      Object.entries(panels).forEach(([key, el]) => {
        el.hidden = key !== next;
        if (toolBtns[key]) toolBtns[key].classList.toggle('is-active', key === next);
      });
      activePanel = next;
      if (next === 'photo') renderPetList();
      if (next === 'size') buildSizePanel();
      syncControls();
      drawOverlay();
      if (next && scroll) panels[next].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    Object.entries(toolBtns).forEach(([key, btn]) => btn.addEventListener('click', () => setActivePanel(key)));
    $$('[data-panel-done]').forEach((btn) => btn.addEventListener('click', () => setActivePanel(null)));

    // Design | On the wall
    const viewBtns = $$('[data-view-option]');
    function syncViewSwitch() {
      viewBtns.forEach((btn) => {
        const on = btn.dataset.viewOption === viewMode;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    viewBtns.forEach((btn) =>
      btn.addEventListener('click', () => {
        if (viewMode === btn.dataset.viewOption) return;
        viewMode = btn.dataset.viewOption;
        wallIndex = 0;
        if (viewMode === 'wall') scenesForFormat().forEach((sc) => loadScene(sc).catch(() => {}));
        syncViewSwitch();
        redraw();
      })
    );
    syncViewSwitch();

    // Background
    const bgBtns = $$('[data-background]');
    bgBtns.forEach((btn) =>
      btn.addEventListener('click', () => {
        snapshot();
        background = btn.dataset.background;
        // Keep the name readable: light text on dark backgrounds and back.
        const darkBg = btn.dataset.textTone === 'light';
        const lightText = textAdjust.color === '#ffffff';
        if (darkBg && !lightText) textAdjust.color = '#ffffff';
        if (!darkBg && lightText) textAdjust.color = '#2b2522';
        changed();
      })
    );

    // Name: font, size, colour, place
    const fontBtns = $$('[data-text-font]');
    fontBtns.forEach((btn) =>
      btn.addEventListener('click', () => {
        snapshot();
        textAdjust.font = btn.dataset.textFont;
        changed();
      })
    );
    $('[data-text-size-down]').addEventListener('click', () => {
      snapshot();
      textAdjust.scale = clamp(textAdjust.scale - SIZE_STEP, SIZE_MIN, SIZE_MAX);
      changed();
    });
    $('[data-text-size-up]').addEventListener('click', () => {
      snapshot();
      textAdjust.scale = clamp(textAdjust.scale + SIZE_STEP, SIZE_MIN, SIZE_MAX);
      changed();
    });
    const colorBtns = $$('[data-text-color]');
    colorBtns.forEach((btn) =>
      btn.addEventListener('click', () => {
        snapshot();
        textAdjust.color = btn.dataset.textColor;
        changed();
      })
    );
    // Above: name at the top, pets on the bottom edge (the default).
    // Below: name near the bottom, pets lifted to make room.
    const BELOW_NAME_Y = 0.88;
    const BELOW_PET_LIFT = 0.19;
    // House: the text sits under the painting by default; "Above" moves it
    // to the top and the painting down a little.
    const HOUSE_TOP_TEXT_Y = 0.1;
    const HOUSE_TEXT_Y = 0.867; // same as the renderer's house default
    const HOUSE_ART_DROP = 0.07;
    $$('[data-text-place]').forEach((btn) =>
      btn.addEventListener('click', () => {
        if (!lastDesign) return;
        snapshot();
        const H = lastDesign.height;
        const below = btn.dataset.textPlace === 'bottom';
        if (isHouse) {
          textAdjust.offsetX = 0;
          textAdjust.offsetY = below ? 0 : (HOUSE_TOP_TEXT_Y - HOUSE_TEXT_Y) * H;
          petAdjusts.forEach((a) => {
            a.offsetY = below ? 0 : HOUSE_ART_DROP * H;
          });
          changed();
          return;
        }
        textAdjust.offsetX = 0;
        textAdjust.offsetY = below ? (BELOW_NAME_Y - 0.17) * H : 0;
        petAdjusts.forEach((a) => {
          a.offsetY = below ? -BELOW_PET_LIFT * H : 0;
        });
        changed();
      })
    );
    $('[data-remove-name]').addEventListener('click', () => {
      nameInput.value = '';
      if (line2Input) line2Input.value = '';
      nameInput.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const fontSampleEls = $$('[data-font-sample]');
    function syncFontSamples() {
      const name = nameInput.value.trim() || 'Name';
      const short = name.length > 9 ? `${name.slice(0, 8)}…` : name;
      fontSampleEls.forEach((el) => {
        el.textContent = short;
      });
    }
    syncFontSamples();

    // Move & size
    $('[data-art-size-down]').addEventListener('click', () => {
      snapshot();
      selectedAdjust().scale = clamp(selectedAdjust().scale - SIZE_STEP, SIZE_MIN, SIZE_MAX);
      changed();
    });
    $('[data-art-size-up]').addEventListener('click', () => {
      snapshot();
      selectedAdjust().scale = clamp(selectedAdjust().scale + SIZE_STEP, SIZE_MIN, SIZE_MAX);
      changed();
    });
    $$('[data-art-rotate]').forEach((btn) =>
      btn.addEventListener('click', () => {
        snapshot();
        selectedAdjust().rotation = (selectedAdjust().rotation || 0) + Number(btn.dataset.artRotate);
        changed();
      })
    );
    $$('[data-art-nudge]').forEach((btn) =>
      btn.addEventListener('click', () => {
        snapshot();
        const dir = btn.dataset.artNudge;
        const dx = dir === 'left' ? -NUDGE_STEP : dir === 'right' ? NUDGE_STEP : 0;
        const dy = dir === 'up' ? -NUDGE_STEP : dir === 'down' ? NUDGE_STEP : 0;
        moveWithin(selectedAdjust(), lastDesign && lastDesign.petRects[selectedPet], dx, dy, false);
        changed();
      })
    );
    $('[data-adjust-undo]').addEventListener('click', undo);
    $('[data-adjust-reset]').addEventListener('click', () => {
      snapshot();
      Art.defaultPetAdjusts(petAdjusts.length).forEach((a, i) => Object.assign(petAdjusts[i], a));
      Object.assign(textAdjust, { scale: 1, offsetX: 0, offsetY: 0 });
      changed();
    });

    function syncControls() {
      artSizeValueEl.textContent = Math.round(selectedAdjust().scale * 100);
      textSizeValueEl.textContent = Math.round(textAdjust.scale * 100);
      if (adjustTitleEl) {
        adjustTitleEl.textContent =
          petAdjusts.length > 1 ? `Pet ${selectedPet + 1} of ${petAdjusts.length} · tap a pet to switch` : 'Move & size';
      }
      bgBtns.forEach((b) => b.classList.toggle('is-active', b.dataset.background === background));
      fontBtns.forEach((b) => b.classList.toggle('is-active', b.dataset.textFont === textAdjust.font));
      colorBtns.forEach((b) => b.classList.toggle('is-active', b.dataset.textColor === textAdjust.color));
      if (bgToolSwatch) bgToolSwatch.style.setProperty('--swatch', background);
    }
    syncControls();

    // Style & size panel: mirrors step 1's choices (step 1 stays the
    // single source of truth; this just calls its select()).
    function buildSizePanel() {
      const F = window.NavaBirdWallFormat;
      const s = window.__nbWallState;
      if (!F || !s) return;
      const formatsEl = $('[data-mini-formats]');
      const sizesEl = $('[data-mini-sizes]');
      formatsEl.innerHTML = '';
      sizesEl.innerHTML = '';
      F.formats().forEach(({ value }) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'nb-wall__chip' + (value === s.formatValue ? ' is-active' : '');
        b.textContent = value;
        b.addEventListener('click', () => F.select(value, null));
        formatsEl.appendChild(b);
      });
      F.sizes().forEach((value) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'nb-wall__chip' + (value === s.sizeValue ? ' is-active' : '');
        const price = F.priceLabel(s.formatValue, value);
        b.innerHTML = `<span></span>${price ? `<small>${price}</small>` : ''}`;
        b.querySelector('span').textContent = value;
        b.addEventListener('click', () => F.select(null, value));
        sizesEl.appendChild(b);
      });
    }

    // --- Your pets ----------------------------------------------------------
    let photoTarget = null;
    function setPhotos(next) {
      const s = window.__instantArtworkState;
      if (!s) return;
      s.photos = next;
      const uploadEl = document.querySelector('instant-artwork-upload');
      if (uploadEl && typeof uploadEl.render === 'function') {
        uploadEl.photos = [...next];
        uploadEl.render();
      }
      renderPetList();
      renderPreview();
    }

    function renderPetList() {
      const photos = currentPhotos();
      petListEl.innerHTML = '';
      photos.forEach((photo, i) => {
        const tile = document.createElement('div');
        tile.className = 'instant-artwork__pet-tile';
        tile.innerHTML = `
          <div class="instant-artwork__pet-thumb"><img alt=""></div>
          <span class="instant-artwork__pet-label">${photos.length > 1 ? `Pet ${i + 1}` : copy.photoLabel || 'Your pet'}</span>
          <div class="instant-artwork__pet-actions">
            <button type="button" class="instant-artwork__pill-btn" data-pet-replace>Replace</button>
            ${photos.length > 1 ? '<button type="button" class="instant-artwork__remove-pet" data-pet-remove>Remove</button>' : ''}
          </div>`;
        tile.querySelector('img').src = photo.previewUrl;
        tile.querySelector('[data-pet-replace]').addEventListener('click', () => {
          photoTarget = i;
          photoInput.click();
        });
        const removeBtn = tile.querySelector('[data-pet-remove]');
        if (removeBtn) {
          removeBtn.addEventListener('click', () => {
            selectedPet = 0;
            setPhotos(photos.filter((_, j) => j !== i));
          });
        }
        petListEl.appendChild(tile);
      });
      if (photos.length < maxPets) {
        const add = document.createElement('div');
        add.className = 'instant-artwork__pet-tile';
        add.innerHTML = `
          <button type="button" class="instant-artwork__pet-add">
            <span class="instant-artwork__pet-add-plus" aria-hidden="true">+</span>
            Add pet
          </button>`;
        add.querySelector('button').addEventListener('click', () => {
          photoTarget = 'add';
          photoInput.click();
        });
        petListEl.appendChild(add);
      }
    }

    photoInput.addEventListener('change', async () => {
      const file = photoInput.files && photoInput.files[0];
      photoInput.value = '';
      const target = photoTarget;
      photoTarget = null;
      if (!file || !file.type.startsWith('image/') || target === null) return;
      try {
        const processed = await window.InstantArtworkPhotoProcessor.processImageFile(file, 500, assets.photoMaxDimension || 2000);
        const next = [...currentPhotos()];
        if (target === 'add') {
          if (next.length >= maxPets) return;
          next.push(processed);
          selectedPet = next.length - 1;
        } else {
          next[target] = processed;
          selectedPet = target;
        }
        setPhotos(next);
      } catch (err) {
        console.error('Portrait photo swap failed', err);
        setError('Could not use that photo — please try a different one.');
      }
    });

    // --- Saving + add to cart ---------------------------------------------
    function setError(message) {
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    function updateButton() {
      addToCartBtn.disabled = !artReady;
    }

    function dataUrlToBase64(dataUrl) {
      return dataUrl.slice(dataUrl.indexOf(',') + 1);
    }

    function base64Bytes(b64) {
      return Math.floor((b64.length * 3) / 4);
    }

    function jpegUnderLimit(canvas, limit = UPLOAD_MAX_BYTES) {
      for (const q of [0.93, 0.88, 0.82, 0.75, 0.68]) {
        const b64 = dataUrlToBase64(canvas.toDataURL('image/jpeg', q));
        if (base64Bytes(b64) <= limit) return b64;
      }
      return dataUrlToBase64(canvas.toDataURL('image/jpeg', 0.6));
    }

    async function uploadDesignFile(kind, dataBase64) {
      for (let attempt = 0; ; attempt++) {
        try {
          const res = await fetch(assets.uploadApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kind, dataBase64 }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.url) {
            console.error(`Portrait upload rejected (${kind})`, res.status, data);
            throw Object.assign(new Error(SAVE_FAILED_MESSAGE), { rejected: true });
          }
          return data.url;
        } catch (err) {
          if (err.rejected || attempt >= 1) throw new Error(SAVE_FAILED_MESSAGE);
        }
      }
    }

    async function originalPhotoBase64(file) {
      const img = await loadImage(URL.createObjectURL(file));
      let scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
      for (;;) {
        const c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * scale);
        c.height = Math.round(img.naturalHeight * scale);
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(img, 0, 0, c.width, c.height);
        const jpg = dataUrlToBase64(c.toDataURL('image/jpeg', 0.85));
        if (base64Bytes(jpg) <= ORIGINAL_MAX_BYTES || scale < 0.3) return jpg;
        scale *= 0.75;
      }
    }

    const originalUploads = new Map();
    function uploadOriginal(photo) {
      if (!originalUploads.has(photo.id)) {
        const promise = originalPhotoBase64(photo.file).then((b64) => uploadDesignFile('original', b64));
        promise.catch(() => originalUploads.delete(photo.id));
        originalUploads.set(photo.id, promise);
      }
      return originalUploads.get(photo.id);
    }

    // Print file: the chosen size at up to 300 DPI, capped so the long side
    // stays at PRINT_MAX_LONG_SIDE px (bigger sizes get a lower DPI).
    function printSpec() {
      const st = wallState();
      const [wIn, hIn] = st.sizeInches;
      const digital = st.formatKey === 'digital';
      // The table is portrait (short side first); a landscape print (house)
      // uses the same size turned sideways.
      const portraitMm = GELATO_SIZES_MM[`${Math.min(wIn, hIn)}x${Math.max(wIn, hIn)}`];
      const mm = portraitMm ? (wIn > hIn ? [portraitMm[1], portraitMm[0]] : portraitMm) : [wIn * 25.4, hIn * 25.4];
      const longIn = Math.max(...mm) / 25.4;
      const dpi = Math.max(PRINT_MIN_DPI, Math.min(PRINT_MAX_DPI, Math.floor(PRINT_MAX_LONG_SIDE / longIn)));
      const px = (v) => Math.round((v / 25.4) * dpi);
      const extraMm = digital ? 0 : st.formatKey === 'canvas' ? CANVAS_WRAP_MM : FRAME_BLEED_MM;
      const extraPx = px(extraMm);
      const trimW = px(mm[0]);
      const trimH = px(mm[1]);
      return {
        mm,
        dpi,
        extraMm,
        extraPx,
        trimW,
        trimH,
        widthPx: trimW + 2 * extraPx,
        heightPx: trimH + 2 * extraPx,
        kind: digital ? 'digital' : st.formatKey === 'canvas' ? 'canvas' : 'frame',
      };
    }

    // The design at the print's exact (metric) shape plus the bleed / canvas
    // wrap: the background and paper continue around the edges (no mirrored
    // copies of the name or ears on the sides), and along the bottom, where
    // the chest runs off the edge, the fur is mirrored so it carries on.
    function renderPrintCanvas(spec) {
      const b = spec.extraPx;
      const pad = (b / spec.trimW) * Art.DESIGN_WIDTH; // in design units
      const out = Art.renderDesign({
        ratio: spec.trimH / spec.trimW,
        background,
        paperImage,
        pets,
        name: designText(),
        text: textAdjust,
        outputWidth: spec.trimW,
        pad,
      }).canvas;
      if (!b) return out;
      const h = spec.trimH;
      const ctx = out.getContext('2d');
      const strip = document.createElement('canvas');
      strip.width = out.width;
      strip.height = b;
      // the last b rows of the visible print...
      strip.getContext('2d').drawImage(out, 0, b + h - b, out.width, b, 0, 0, out.width, b);
      // ...mirrored into the bottom wrap
      ctx.save();
      ctx.translate(0, 2 * b + h);
      ctx.scale(1, -1);
      ctx.drawImage(strip, 0, 0);
      ctx.restore();
      return out;
    }

    function printSpecText(spec) {
      const cm = (v) => `${Math.round(v / 10)}`;
      const size = `${cm(spec.mm[0])}×${cm(spec.mm[1])} cm`;
      const extra =
        spec.kind === 'canvas'
          ? ` + ${spec.extraMm} mm wrap per side (slim 2 cm canvas; background continues round the sides)`
          : spec.kind === 'frame'
            ? ` + ${spec.extraMm} mm bleed per side`
            : '';
      return `${size}${extra} · ${spec.widthPx}×${spec.heightPx} px · ${spec.dpi} DPI`;
    }

    const addLabelEl = addToCartBtn.querySelector('span') || addToCartBtn;
    const ADD_LABEL = addLabelEl.textContent;

    // What they're buying, above the button (the price is in the "Subtotal:"
    // line right under it, kept up to date by navabird-portrait-format.js).
    const summaryEl = $('[data-summary]');
    const summaryText = $('[data-summary-text]');
    function syncSummary() {
      const st = window.__nbWallState;
      if (!st || !st.formatValue) return;
      const digital = st.formatKey === 'digital';
      summaryText.textContent = digital ? st.formatValue : `${st.formatValue} · ${st.sizeValue}`;
      summaryEl.hidden = false;
    }
    $('[data-summary-change]').addEventListener('click', () => {
      if (activePanel !== 'size') setActivePanel('size');
      else panels.size.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    document.addEventListener('nb-wall:selection-changed', syncSummary);
    step.addEventListener('instant-artwork:step-shown', syncSummary);
    syncSummary();

    addToCartBtn.addEventListener('click', async () => {
      const s = wallState();
      const hasPortraitOptions = productData.options.some((o) => o.name === 'Format');
      if (!hasPortraitOptions || !s.variant) {
        setError(
          hasPortraitOptions
            ? 'That style and size isn’t available — please pick another.'
            : 'This product isn’t set up for portraits yet (it needs Format and Size options).'
        );
        return;
      }
      if (!pets.length) {
        setError('Your design is still being painted — give it a second and try again.');
        return;
      }

      addToCartBtn.disabled = true;
      addToCartBtn.dataset.busy = '1';
      addLabelEl.textContent = 'Saving your design…';
      setError(null);

      try {
        const spec = printSpec();
        const print = { canvas: renderPrintCanvas(spec) };
        const preview = renderDesignAt(900);
        const mockup = renderMockup(1000, 700).canvas;
        const photos = currentPhotos();
        const [printUrl, artUrl, mockupUrl, ...originalUrls] = await Promise.all([
          uploadDesignFile('portrait', jpegUnderLimit(print.canvas)),
          uploadDesignFile('art', dataUrlToBase64(preview.canvas.toDataURL('image/jpeg', 0.88))),
          uploadDesignFile('mockup', dataUrlToBase64(mockup.toDataURL('image/jpeg', 0.85))),
          ...photos.map(uploadOriginal),
        ]);

        addLabelEl.textContent = 'Adding to cart…';
        const name = nameInput.value.trim();
        const properties = {
          _design_id:
            window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `design-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          ...(name ? { [copy.nameProperty || 'Pet Name(s)']: name } : {}),
          ...(line2Input && line2Input.value.trim() ? { [copy.line2Property || 'Text line 2']: line2Input.value.trim() } : {}),
          _print_file: printUrl,
          _print_spec: printSpecText(spec),
          _art_url: artUrl,
          _mockup_url: mockupUrl,
          _original_photo_urls: originalUrls.join('\n'),
          _design_details: `Background ${background} · ${(Art.FONTS[textAdjust.font] || {}).label || textAdjust.font} font · text ${textAdjust.color}`,
        };

        const cartDrawer = document.querySelector('cart-drawer');
        const body = { id: s.variant.id, quantity: 1, properties };
        if (cartDrawer) {
          body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status || data.errors) {
          console.error('Cart add rejected', response.status, data);
          throw new Error(data.description || "Sorry, we couldn't add this design to your cart. Please try again.");
        }
        if (cartDrawer) {
          cartDrawer.setActiveElement(addToCartBtn);
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(data);
          addToCartBtn.disabled = false;
          delete addToCartBtn.dataset.busy;
          addLabelEl.textContent = ADD_LABEL;
        } else {
          window.location.href = window.routes.cart_url;
        }
      } catch (err) {
        console.error('Portrait add to cart failed', err);
        setError(err.message || 'Something went wrong adding this to your cart — please try again.');
        addToCartBtn.disabled = false;
        delete addToCartBtn.dataset.busy;
        addLabelEl.textContent = ADD_LABEL;
      }
    });

    updateButton();
    prepareAssets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
